"use client";

import { useCallback, useEffect, useState, useSyncExternalStore } from "react";
import { cn } from "@v1/ui/utils";
import { Sun, Moon } from "lucide-react";

type ThemeMode = "mixed" | "dark";
const THEME_CHANGE_EVENT = "mlx-theme-change";
const THEME_OPTIONS: { mode: ThemeMode; icon: typeof Sun; label: string }[] = [
  { mode: "mixed", icon: Sun, label: "Light" },
  { mode: "dark", icon: Moon, label: "Dark" },
];

function getThemeFromDOM(): ThemeMode {
  if (typeof document === "undefined") return "mixed";
  return document.documentElement.classList.contains("dark") ? "dark" : "mixed";
}

function applyTheme(mode: ThemeMode) {
  if (typeof document === "undefined") return;
  const root = document.documentElement;
  root.classList.remove("theme-mixed", "dark");
  root.classList.add(mode === "dark" ? "dark" : "theme-mixed");
  window.dispatchEvent(new Event(THEME_CHANGE_EVENT));
}

function subscribeToThemeChange(onStoreChange: () => void) {
  window.addEventListener(THEME_CHANGE_EVENT, onStoreChange);
  return () => window.removeEventListener(THEME_CHANGE_EVENT, onStoreChange);
}

export function ThemeToggle({
  variant = "auto",
}: {
  variant?: "auto" | "dark" | "light";
}) {
  const mode = useSyncExternalStore(subscribeToThemeChange, getThemeFromDOM, () => "mixed");
  const [isOpen, setIsOpen] = useState(false);

  const handleSelect = useCallback((newMode: ThemeMode) => {
    applyTheme(newMode);
    setIsOpen(false);
    try {
      localStorage.setItem("theme", newMode);
    } catch {}
  }, []);

  // Close dropdown on outside click
  useEffect(() => {
    if (!isOpen) return;
    const handleClick = () => setIsOpen(false);
    document.addEventListener("click", handleClick);
    return () => document.removeEventListener("click", handleClick);
  }, [isOpen]);

  const CurrentIcon = THEME_OPTIONS.find((o) => o.mode === mode)?.icon || Sun;

  // Detect if we're in light mode for styling. The variant prop can
  // override: "dark" forces dark styling, "light" forces light styling.
  const isInLightMode =
    variant === "light" ? true : variant === "dark" ? false : mode === "mixed";

  return (
    <div className="relative" onClick={(e) => e.stopPropagation()}>
      <button
        type="button"
        aria-label="Toggle theme"
        onClick={() => setIsOpen(!isOpen)}
        className={cn(
          "inline-flex items-center justify-center hover:opacity-80 focus:outline-none focus:ring-2 p-1.5 rounded-md transition-colors",
          !isInLightMode && "text-white focus:ring-white/40",
          !isInLightMode && isOpen && "bg-white/10",
          isInLightMode && "text-stone-700 focus:ring-stone-400/40",
          isInLightMode && isOpen && "bg-stone-200/80",
        )}
      >
        <CurrentIcon size={18} />
      </button>

      {isOpen && (
        <div
          className={cn(
            "absolute top-full right-0 mt-2 w-36 shadow-xl rounded-lg py-1 z-50 animate-in fade-in slide-in-from-top-2 duration-150",
            !isInLightMode && "bg-zinc-900 border border-zinc-700",
            isInLightMode && "bg-white border border-stone-200",
          )}
        >
          {THEME_OPTIONS.map((option) => (
            <button
              type="button"
              key={option.mode}
              onClick={() => handleSelect(option.mode)}
              className={cn(
                "w-full flex items-center gap-2 px-3 py-2 text-[13px] transition-colors text-left",
                !isInLightMode &&
                  mode === option.mode &&
                  "text-white bg-white/10",
                !isInLightMode &&
                  mode !== option.mode &&
                  "text-zinc-400 hover:text-white hover:bg-white/5",
                isInLightMode &&
                  mode === option.mode &&
                  "text-stone-900 bg-stone-100",
                isInLightMode &&
                  mode !== option.mode &&
                  "text-stone-600 hover:text-stone-900 hover:bg-stone-50",
              )}
            >
              <option.icon size={14} />
              {option.label}
              {mode === option.mode && (
                <span
                  className={cn(
                    "ml-auto",
                    !isInLightMode && "text-emerald-500",
                    isInLightMode && "text-violet-600",
                  )}
                >
                  ✓
                </span>
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
